import { View, Text, StyleSheet, Svg, G, Path } from '@react-pdf/renderer'
import { colors, fonts, type, space, BRAND_ACCENT } from './theme'

// ─── Shared base styles ──────────────────────────────────────────────────────
// All style objects are created here so they are instantiated once and shared
// across components, not re-created on every render.

const s = StyleSheet.create({
  // ── Rules ────────────────────────────────────────────────────────────────
  rule: {
    height: 0.75,
    backgroundColor: colors.line,
  },
  ruleAccent: {
    height: 2,
    backgroundColor: BRAND_ACCENT,
    borderRadius: 1,
  },
  ruleSubtle: {
    height: 0.5,
    backgroundColor: colors.lineSubtle,
  },

  // ── Section label ─────────────────────────────────────────────────────────
  // Uppercase, tracked, muted — used above chart, analysis, etc.
  sectionLabel: {
    fontFamily: fonts.sans,
    fontSize: type.size.xs,
    fontWeight: type.weight.bold,
    color: colors.fgSecondary,
    letterSpacing: 1,
    marginBottom: space[2],
  },

  // ── Body text ─────────────────────────────────────────────────────────────
  body: {
    fontFamily: fonts.serif,
    fontSize: type.size.base,
    fontWeight: type.weight.normal,
    lineHeight: type.leading.relaxed,
    color: colors.fg,
  },

  // ── Caption / metadata ────────────────────────────────────────────────────
  caption: {
    fontFamily: fonts.sans,
    fontSize: type.size.xs,
    color: colors.fgMuted,
  },

  // ── Monospace ─────────────────────────────────────────────────────────────
  mono: {
    fontFamily: fonts.mono,
    fontSize: type.size.sm,
    color: colors.fgSecondary,
  },
})

// ─── Rule ────────────────────────────────────────────────────────────────────
export function Rule({ accent = false, subtle = false }: { accent?: boolean; subtle?: boolean }) {
  if (accent)  return <View style={s.ruleAccent} />
  if (subtle)  return <View style={s.ruleSubtle} />
  return <View style={s.rule} />
}

// ─── Spacer ───────────────────────────────────────────────────────────────────
export function Spacer({ size }: { size: keyof typeof space }) {
  return <View style={{ height: space[size] }} />
}

// ─── Section label ────────────────────────────────────────────────────────────
export function SectionLabel({ children }: { children: string }) {
  return <Text style={s.sectionLabel}>{children.toUpperCase()}</Text>
}

// ─── Body text ────────────────────────────────────────────────────────────────
export function BodyText({
  children,
  italic = false,
}: {
  children: string
  italic?: boolean
}) {
  return (
    <Text style={[s.body, italic ? { fontStyle: 'italic' } : {}]}>
      {children}
    </Text>
  )
}

// ─── Caption ──────────────────────────────────────────────────────────────────
export function Caption({ children }: { children: string }) {
  return <Text style={s.caption}>{children}</Text>
}

// ─── WIRe logo mark ──────────────────────────────────────────────────────────
// The "W" mark rendered as react-pdf SVG primitives.
// `height` controls the rendered height in pt; width scales proportionally.
// Original viewBox: 125.409 × 98.041
export function WireLogo({
  height = 20,
  color = BRAND_ACCENT,
}: {
  height?: number
  color?: string
}) {
  const aspect = 125.409 / 98.041
  const width = height * aspect
  return (
    <Svg width={width} height={height} viewBox="0 0 125.409 98.041">
      <G transform="translate(0, 0)">
        <Path d="M0,0 L48.06,98.041 L64.4,69.206 L30.278,0 Z" fill={color} />
      </G>
      <G transform="translate(40.35, 0)">
        <Path d="M0,0 L48.059,98.041 L64.4,69.206 L30.277,0 Z" fill={color} />
      </G>
      <G transform="translate(80.762, 0)">
        <Path d="M0,0 L31.149,63 L44.649,39.182 L25.672,0 Z" fill={color} />
      </G>
    </Svg>
  )
}
